package com.trevorwiebe.timesheet.loads.presentation

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.fullscreenDialogProperties
import com.trevorwiebe.timesheet.core.presentation.common.renderPdfPages
import com.trevorwiebe.timesheet.loads.domain.model.LoadAttachment

// Keeps pages readable on wide web windows instead of stretching them edge to edge.
private val MAX_PAGE_WIDTH = 850.dp

/**
 * Full-screen pages of a document's PDF, with an optional delete action in the top bar.
 * [pdf] is null while the PDF is still downloading.
 */
@Composable
internal fun DocumentViewer(
    document: LoadAttachment,
    pdf: ByteArray?,
    isSaving: Boolean,
    onDismiss: () -> Unit,
    onDelete: (() -> Unit)? = null,
) {
    var confirmDelete by remember { mutableStateOf(false) }
    var pages by remember(pdf) { mutableStateOf<List<ImageBitmap>?>(null) }
    var failed by remember(pdf) { mutableStateOf(false) }
    LaunchedEffect(pdf) {
        if (pdf != null) {
            runCatching { renderPdfPages(pdf) }
                .onSuccess { pages = it }
                .onFailure { failed = true }
        }
    }

    Dialog(onDismissRequest = onDismiss, properties = fullscreenDialogProperties()) {
        Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
            Column(modifier = Modifier.fillMaxSize().statusBarsPadding().navigationBarsPadding()) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp, vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Default.Close, contentDescription = "Close")
                    }
                    Text(
                        text = document.name,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    if (isSaving) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(20.dp),
                            strokeWidth = 2.dp,
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }
                    if (onDelete != null) {
                        IconButton(onClick = { confirmDelete = true }, enabled = !isSaving) {
                            Icon(Icons.Default.Delete, contentDescription = "Delete document", tint = MaterialTheme.colorScheme.error)
                        }
                    }
                }
                val rendered = pages
                Box(
                    modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.surfaceVariant),
                    contentAlignment = Alignment.Center,
                ) {
                    when {
                        failed -> Text("Couldn't open this document.", color = MaterialTheme.colorScheme.error)
                        rendered == null -> CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                        else -> LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(12.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            itemsIndexed(rendered) { index, page ->
                                Image(
                                    bitmap = page,
                                    contentDescription = "Page ${index + 1}",
                                    contentScale = ContentScale.FillWidth,
                                    modifier = Modifier
                                        .widthIn(max = MAX_PAGE_WIDTH)
                                        .fillMaxWidth()
                                        .aspectRatio(page.width.toFloat() / page.height),
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    NativeDialog(
        visible = confirmDelete,
        title = "Delete Document",
        message = "Delete this document? This action cannot be undone.",
        confirmText = "Delete",
        onConfirm = {
            confirmDelete = false
            onDelete?.invoke()
        },
        dismissText = "Cancel",
        onDismiss = { confirmDelete = false },
    )
}
